import { useCallback, useMemo, useState } from 'react';
import { BlockStack, Box, Button, Popover, RadioButton, Text } from '@/lib/polaris';
import { ChannelsIcon } from '@/lib/polaris-icons';

export type ChannelValue = 'all' | 'pos' | 'ecommerce' | 'kds' | 'other';

export function PolarisChannelPopover(props: {
  value: ChannelValue;
  onChange: (next: ChannelValue) => void;
  options?: Array<{ value: ChannelValue; label: string }>;
  /** 'button' = botón estándar, 'kpi' = cuadro estilo KPI (label + valor en 2 líneas),
   *  'tertiary' = botón terciario con icono de canales (estilo PeriodSelector del KpiBar) */
  activatorVariant?: 'button' | 'kpi' | 'tertiary';
}) {
  const { value, onChange, activatorVariant = 'button' } = props;
  const options =
    props.options ??
    ([
      { value: 'all', label: 'Todos' },
      { value: 'pos', label: 'POS' },
      { value: 'ecommerce', label: 'Tienda online' },
      { value: 'kds', label: 'KDS' },
    ] as const);

  const [open, setOpen] = useState(false);

  const label = useMemo(() => {
    return options.find((o) => o.value === value)?.label ?? 'Todos';
  }, [options, value]);

  const openPopover = useCallback(() => setOpen(true), []);

  return (
    <Popover
      active={open}
      preferredAlignment="left"
      preferredPosition="below"
      activator={
        activatorVariant === 'kpi' ? (
          <Box
            as="button"
            type="button"
            minWidth="140px"
            padding="300"
            background="bg-surface"
            borderRadius="100"
            onClick={openPopover}
            aria-label={`Canal seleccionado: ${label}`}
            style={{ border: 'none', cursor: 'pointer', textAlign: 'left' }}
          >
            <BlockStack gap="100">
              <Text variant="bodySm" tone="subdued" as="span">
                Canal
              </Text>
              <Text variant="headingLg" as="span">
                {label}
              </Text>
            </BlockStack>
          </Box>
        ) : activatorVariant === 'tertiary' ? (
          // Estilo reciclado del PeriodSelector del KpiBar: botón terciario + icono de canales.
          <Button variant="tertiary" icon={ChannelsIcon} onClick={openPopover}>
            {label}
          </Button>
        ) : (
          <Button variant="secondary" size="slim" disclosure onClick={openPopover}>
            {label}
          </Button>
        )
      }
      onClose={() => setOpen(false)}
      autofocusTarget="first-node"
    >
      <Box padding="300" minWidth="220px">
        <BlockStack gap="300">
          <Text variant="headingSm" as="h3">
            Canal
          </Text>
          <BlockStack gap="200">
            {options.map((o) => (
              <RadioButton
                key={o.value}
                label={o.label}
                checked={value === o.value}
                id={`channel-${o.value}`}
                name="channel-filter"
                onChange={() => {
                  onChange(o.value);
                  setOpen(false);
                }}
              />
            ))}
          </BlockStack>
        </BlockStack>
      </Box>
    </Popover>
  );
}

